import { create, type StateCreator } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

export interface MswSlice {
    mswEnabled: boolean
    /** Map of GraphQL operation name → raw JSON string for the full response body */
    mswOverrides: Record<string, string>
    /** Saved scenario name → snapshot of operation overrides */
    mswScenarios: Record<string, Record<string, string>>
    /** Operation names observed by the catch-all GraphQL MSW handler */
    mswOperationNames: string[]
    /** Latest mocked/default response body captured per operation name */
    mswOperationResponses: Record<string, string>
}

export const createMswSlice: StateCreator<MswSlice, [], [], MswSlice> = () => ({
    mswEnabled: false,
    mswOverrides: {},
    mswScenarios: {},
    mswOperationNames: [],
    mswOperationResponses: {},
})

export const useMswStore = create<MswSlice>()(
    persist(
        devtools(createMswSlice, {
            name: 'MswStore',
        }),
        { name: 'msw-enabled' },
    ),
)

export const useMswEnabled = () => useMswStore((state) => state.mswEnabled)
export const useMswOverrides = () => useMswStore((state) => state.mswOverrides)
export const useMswScenarios = () => useMswStore((state) => state.mswScenarios)
export const useMswOperationNames = () => useMswStore((state) => state.mswOperationNames)
export const useMswOperationResponses = () => useMswStore((state) => state.mswOperationResponses)
export const getMswEnabledState = () => useMswStore.getState().mswEnabled
export const setMswEnabled = (state: boolean) => useMswStore.setState({ mswEnabled: state })
export const toggleMswEnabled = () => setMswEnabled(!getMswEnabledState())

export const addMswOperationName = (operationName: string): void => {
    if (!operationName.trim()) {
        return
    }

    useMswStore.setState((s) => {
        if (s.mswOperationNames.includes(operationName)) {
            return s
        }

        return {
            mswOperationNames: [...s.mswOperationNames, operationName].sort((a, b) => a.localeCompare(b)),
        }
    })
}

export const clearMswOperationNames = (): void => {
    useMswStore.setState({ mswOperationNames: [] })
}

export const setMswOperationResponse = (operationName: string, response: unknown): void => {
    if (!operationName.trim()) {
        return
    }

    let raw = ''
    try {
        raw = JSON.stringify(response, null, 2)
    } catch {
        return
    }

    useMswStore.setState((s) => ({
        mswOperationResponses: {
            ...s.mswOperationResponses,
            [operationName]: raw,
        },
    }))
}

export const clearMswOperationResponses = (): void => {
    useMswStore.setState({ mswOperationResponses: {} })
}

export const getMswOverride = (operationName: string): unknown | null => {
    const raw = useMswStore.getState().mswOverrides[operationName]
    if (!raw) return null
    try {
        return JSON.parse(raw)
    } catch {
        return null
    }
}

export const setMswOverride = (operationName: string, json: string): void => {
    useMswStore.setState((s) => ({
        mswOverrides: { ...s.mswOverrides, [operationName]: json },
    }))
}

export const clearMswOverride = (operationName: string): void => {
    useMswStore.setState((s) => {
        const next = { ...s.mswOverrides }
        delete next[operationName]
        return { mswOverrides: next }
    })
}

export const replaceMswOverrides = (nextOverrides: Record<string, string>): void => {
    useMswStore.setState({ mswOverrides: { ...nextOverrides } })
}

export const saveMswScenario = (scenarioName: string): void => {
    const name = scenarioName.trim()
    if (!name) {
        return
    }

    useMswStore.setState((s) => ({
        mswScenarios: {
            ...s.mswScenarios,
            [name]: { ...s.mswOverrides },
        },
    }))
}

export const applyMswScenario = (scenarioName: string): void => {
    const name = scenarioName.trim()
    if (!name) {
        return
    }

    const scenario = useMswStore.getState().mswScenarios[name]
    if (!scenario) {
        return
    }

    useMswStore.setState({ mswOverrides: { ...scenario } })
}

export const deleteMswScenario = (scenarioName: string): void => {
    const name = scenarioName.trim()
    if (!name) {
        return
    }

    useMswStore.setState((s) => {
        const next = { ...s.mswScenarios }
        delete next[name]
        return { mswScenarios: next }
    })
}

export const clearMswScenarios = (): void => {
    useMswStore.setState({ mswScenarios: {} })
}
